import { useEffect, useState } from 'react';
import {
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import type { OrigemPedidoAdmin } from '../../../api';
import { IconeOrigemPedido, ROTULO_ORIGEM_PEDIDO } from '../../../components/admin/icone-modo';
import { periodosPedido, ROTULO_PERIODO, type PeriodoPedido } from './periodo';

/** Origens oferecidas no filtro — as mesmas que o pedido pode ter. */
const ORIGENS: OrigemPedidoAdmin[] = ['MESA_COMANDA', 'DELIVERY', 'TOTEM'];

export interface EstadoFiltrosPedidos {
  /**
   * Termo JÁ APLICADO — o que o usuário digita fica só dentro do campo até ele
   * apertar Enter. A grade de pedidos cresce sem teto e a busca varre o
   * `payload_json`, então buscar a cada tecla sairia caro à toa.
   */
  busca: string;
  /** `''` = todas as origens. */
  origem: OrigemPedidoAdmin | '';
  periodo: PeriodoPedido;
  /** Dias do modo personalizado (`yyyy-mm-dd`). */
  de: string;
  ate: string;
}

export const FILTROS_PEDIDOS_VAZIOS: EstadoFiltrosPedidos = {
  busca: '',
  origem: '',
  periodo: 'TODOS',
  de: '',
  ate: '',
};

export function temFiltroAtivo(filtros: EstadoFiltrosPedidos): boolean {
  return filtros.busca.trim() !== '' || filtros.origem !== '' || filtros.periodo !== 'TODOS';
}

interface Props {
  valor: EstadoFiltrosPedidos;
  aoMudar: (novo: EstadoFiltrosPedidos) => void;
}

export function FiltrosPedidos({ valor, aoMudar }: Props): JSX.Element {
  /** Texto em digitação — só vira filtro no Enter (ou no botão da lupa). */
  const [texto, setTexto] = useState(valor.busca);
  const datasInvertidas = valor.de !== '' && valor.ate !== '' && valor.ate < valor.de;
  const buscaPendente = texto.trim() !== valor.busca;

  // Ressincroniza quando o termo é limpo/alterado de fora (ex.: "Limpar filtros").
  useEffect(() => setTexto(valor.busca), [valor.busca]);

  function alterar<C extends keyof EstadoFiltrosPedidos>(campo: C, novo: EstadoFiltrosPedidos[C]): void {
    aoMudar({ ...valor, [campo]: novo });
  }

  function aplicarBusca(termo: string): void {
    setTexto(termo);
    if (termo.trim() !== valor.busca) alterar('busca', termo.trim());
  }

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <TextField
          placeholder="Buscar por cliente, SEQ ou código do pedido"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') aplicarBusca(texto);
          }}
          size="small"
          fullWidth
          sx={{ flex: 1, minWidth: 0 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Tooltip title="Buscar">
                  <IconButton size="small" aria-label="Buscar" edge="start" onClick={() => aplicarBusca(texto)}>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
            endAdornment: texto !== '' && (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Limpar busca" edge="end" onClick={() => aplicarBusca('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          label="Origem"
          value={valor.origem}
          onChange={(e) => alterar('origem', e.target.value as OrigemPedidoAdmin | '')}
          size="small"
          sx={{ width: { xs: '100%', md: 200 }, flexShrink: 0 }}
        >
          <MenuItem value="">Todas as origens</MenuItem>
          {ORIGENS.map((origem) => (
            <MenuItem key={origem} value={origem}>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconeOrigemPedido origem={origem} />
                <span>{ROTULO_ORIGEM_PEDIDO[origem]}</span>
              </Stack>
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Período"
          value={valor.periodo}
          onChange={(e) => alterar('periodo', e.target.value as PeriodoPedido)}
          size="small"
          sx={{ width: { xs: '100%', md: 200 }, flexShrink: 0 }}
        >
          {periodosPedido.map((periodo) => (
            <MenuItem key={periodo} value={periodo}>
              {ROTULO_PERIODO[periodo]}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {buscaPendente && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          Pressione Enter para buscar
        </Typography>
      )}

      {valor.periodo === 'PERSONALIZADO' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1.5 }}>
          <TextField
            label="De"
            type="date"
            value={valor.de}
            onChange={(e) => alterar('de', e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            helperText="Vazio = desde o primeiro pedido"
            sx={{ width: { xs: '100%', sm: 220 } }}
          />
          <TextField
            label="Até"
            type="date"
            value={valor.ate}
            onChange={(e) => alterar('ate', e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            error={datasInvertidas}
            helperText={datasInvertidas ? 'Deve ser depois do início' : 'Vazio = até hoje'}
            sx={{ width: { xs: '100%', sm: 220 } }}
          />
        </Stack>
      )}

      {temFiltroAtivo(valor) && (
        <Button
          size="small"
          startIcon={<FilterAltOffIcon />}
          onClick={() => aoMudar(FILTROS_PEDIDOS_VAZIOS)}
          sx={{ mt: 1.5 }}
        >
          Limpar filtros
        </Button>
      )}
    </Paper>
  );
}
